// Logica di selezione varianti in stile Amazon (colore > memoria > condizione)
// e calcolo prezzi. Condivisa dalla pagina prodotto.

export const GRADE_ORDER = ["nuovo", "like_new", "used"];

export const GRADE_LABELS = {
  nuovo: "Nuovo",
  like_new: "Come Nuovo",
  used: "Usato",
};

// Descrizione mostrata sotto la condizione scelta.
export const GRADE_DESC = {
  nuovo: "Prodotto nuovo e sigillato, mai utilizzato.",
  like_new: "Nessun segno visibile. Come nuovo.",
  used: "Lievi segni d'uso. Funzionalità identica.",
};

// Varianti selezionabili: quelle attive. Se nessuna è attiva, ricadi su tutte.
export function activeVariants(product) {
  const all = product?.variants || [];
  const act = all.filter((v) => v.active !== false);
  return act.length ? act : all;
}

export function isOnSale(v, now = Date.now()) {
  if (!v) return false;
  const sp = v.sale_price;
  if (sp == null || sp === "") return false;
  const n = Number(sp);
  if (!(n > 0)) return false;
  if (v.sale_start && now < Date.parse(v.sale_start)) return false;
  if (v.sale_end && now > Date.parse(v.sale_end)) return false;
  const price = Number(v.price);
  return Number.isFinite(price) ? n < price : true;
}

export function effectivePrice(v) {
  if (!v) return null;
  return isOnSale(v) ? Number(v.sale_price) : Number(v.price);
}

// Colori realmente presenti tra le varianti selezionabili.
export function colorList(product) {
  const pool = activeVariants(product);
  return (product?.colors || []).filter((c) => pool.some((v) => v.color_id === c.id));
}

// Tutte le memorie presenti (numeriche, ordinate).
export function memoryList(product) {
  const pool = activeVariants(product);
  const set = [...new Set(pool.map((v) => v.memory).filter((m) => m != null && m !== ""))];
  return set.sort((a, b) => Number(a) - Number(b));
}

// Gradi presenti nell'ordine canonico.
export function gradeList(product) {
  const pool = activeVariants(product);
  return GRADE_ORDER.filter((g) => pool.some((v) => v.grade === g));
}

export function hasColors(product) {
  return colorList(product).length > 0;
}
export function hasMemory(product) {
  return memoryList(product).length > 0;
}

// Variante che corrisponde alla selezione (le dimensioni assenti vengono ignorate).
export function findVariant(product, sel) {
  const pool = activeVariants(product);
  const wc = hasColors(product);
  const wm = hasMemory(product);
  return (
    pool.find(
      (v) =>
        (!wc || v.color_id === sel.color_id) &&
        (!wm || v.memory === sel.memory) &&
        v.grade === sel.grade
    ) || null
  );
}

// Abilitazione a cascata: colore libero, memoria filtrata dal colore,
// condizione filtrata da colore+memoria.
export function colorEnabled(product, cid) {
  return activeVariants(product).some((v) => v.color_id === cid);
}
export function memoryEnabled(product, sel, m) {
  const wc = hasColors(product);
  return activeVariants(product).some((v) => (!wc || v.color_id === sel.color_id) && v.memory === m);
}
export function gradeEnabled(product, sel, g) {
  const wc = hasColors(product);
  const wm = hasMemory(product);
  return activeVariants(product).some(
    (v) =>
      (!wc || v.color_id === sel.color_id) &&
      (!wm || v.memory === sel.memory) &&
      v.grade === g
  );
}

// Normalizza la selezione a cascata: se una scelta rende impossibile la
// combinazione, si sposta automaticamente alla variante disponibile più vicina.
export function normalizeSelection(product, sel) {
  const pool = activeVariants(product);
  const colors = colorList(product);
  const wc = colors.length > 0;
  const wm = memoryList(product).length > 0;
  const next = { color_id: sel?.color_id ?? null, memory: sel?.memory ?? null, grade: sel?.grade ?? null };

  if (wc) {
    if (!colors.some((c) => c.id === next.color_id)) next.color_id = colors[0].id;
  } else {
    next.color_id = null;
  }

  const memScope = pool.filter((v) => !wc || v.color_id === next.color_id);
  const availMem = [...new Set(memScope.map((v) => v.memory).filter((m) => m != null && m !== ""))]
    .sort((a, b) => Number(a) - Number(b));
  if (wm) {
    if (!availMem.includes(next.memory)) next.memory = availMem.length ? availMem[0] : null;
  } else {
    next.memory = null;
  }

  const gradeScope = pool.filter(
    (v) => (!wc || v.color_id === next.color_id) && (!wm || v.memory === next.memory)
  );
  const availGrades = GRADE_ORDER.filter((g) => gradeScope.some((v) => v.grade === g));
  if (!availGrades.includes(next.grade)) next.grade = availGrades.length ? availGrades[0] : null;

  return next;
}

// Selezione iniziale: parte dalla variante col prezzo effettivo più basso.
export function defaultSelection(product) {
  const pool = activeVariants(product);
  let base = pool[0];
  let min = Infinity;
  for (const v of pool) {
    const p = effectivePrice(v);
    if (p != null && p < min) {
      min = p;
      base = v;
    }
  }
  return normalizeSelection(product, {
    color_id: base?.color_id ?? null,
    memory: base?.memory ?? null,
    grade: base?.grade ?? null,
  });
}

// Foto del colore selezionato seguite dalle foto comuni (senza duplicati).
export function galleryPhotos(product, colorId) {
  const photos = product?.photos || [];
  const bySort = (a, b) =>
    (b.is_primary ? 1 : 0) - (a.is_primary ? 1 : 0) || (a.sort_order ?? 0) - (b.sort_order ?? 0);
  const forColor = colorId ? photos.filter((p) => p.color_id === colorId).sort(bySort) : [];
  const common = photos.filter((p) => !p.color_id).sort(bySort);
  const seen = new Set();
  return [...forColor, ...common].filter((p) => {
    if (seen.has(p.id)) return false;
    seen.add(p.id);
    return true;
  });
}

// Trasformazioni Cloudinary. e_trim (step separato, concatenato con "/") rifila
// i bordi vuoti; poi c_pad + b_transparent riporta ogni foto allo stesso formato
// quadrato. q_auto:best mantiene alta la qualità/nitidezza.
export const CLD_MAIN = "e_trim/c_pad,w_1600,h_1600,b_transparent,f_auto,q_auto:best";
export const CLD_THUMB = "e_trim/c_pad,w_400,h_400,b_transparent,f_auto,q_auto:best";
export const CLD_FULL = "e_trim/c_pad,w_2200,h_2200,b_transparent,f_auto,q_auto:best";
export const CLD_CARD = "e_trim/c_pad,w_800,h_800,b_transparent,f_auto,q_auto:best";

// Trasforma un URL Cloudinary applicando una trasformazione (dimensioni/qualità).
export function cloudinaryThumb(url, t = CLD_MAIN) {
  if (!url || typeof url !== "string" || !url.includes("/upload/")) return url;
  return url.replace("/upload/", `/upload/${t}/`);
}

// Foto principale del prodotto per la card catalogo (is_primary, poi la prima).
export function primaryPhoto(product) {
  const photos = product?.photos || [];
  if (!photos.length) return null;
  return photos.find((p) => p.is_primary) || photos[0];
}

export function variantLabel(product, v) {
  if (!v) return "";
  const parts = [];
  const c = (product?.colors || []).find((x) => x.id === v.color_id);
  if (c) parts.push(c.name);
  if (v.memory) parts.push(`${v.memory} GB`);
  if (v.grade) parts.push(GRADE_LABELS[v.grade] || v.grade);
  return parts.join(" · ");
}
